import { API } from "./API";

// API layer for the two Email-linked tabs (Part 1 customer, Part 2 buy side).
//
// Kept in its own module rather than added to API.ts so this feature adds no
// diff to that file. It reuses the same axios instance, so the auth token
// interceptor and 401 handling apply unchanged.

export type EmailLinkType = "quote" | "dealer";
export type EmailLinkStatus = "linked" | "pending" | "rejected";

// The Quotes (Sales View) page saves the models on the quote it's showing
// under this key whenever they change. The Dealer Email tab reads it once on
// a fresh visit (no ?model= on the URL yet) so switching over from a quote
// lands the multi-select on the same models, without the Quotes page having
// to build a matching URL itself.
export const DEALER_EMAIL_MODELS_STORAGE_KEY = "quote_dealer_email_models";

export interface EmailAttachmentMeta {
  id: number;
  name: string;
  contentType: string | null;
  size: number | null;
  isInline: boolean;
}

export interface EmailLinkMeta {
  id: number;
  linkType: EmailLinkType;
  quoteId: string | null;
  model: string | null;
  wtbRequestId: string | null;
  status: EmailLinkStatus;
  matchSource: string;
  matchField: string | null;
  matchedText: string | null;
  confidence: number | null;
}

export interface EmailMessageRecord {
  id: number;
  providerMessageId: string;
  conversationId: string | null;
  subject: string;
  fromEmail: string | null;
  fromName: string | null;
  toEmails: string[];
  ccEmails: string[];
  /** "in" = from the customer/dealer, "out" = our reply. */
  direction: "in" | "out";
  sentAt: string | null;
  receivedAt: string | null;
  bodyPreview: string;
  bodyHtml: string;
  hasAttachments: boolean;
  /** Always null over IMAP - there is no web link to a message. */
  webLink: string | null;
  folder: string | null;
  attachments: EmailAttachmentMeta[];
  link: EmailLinkMeta | null;
}

export interface EmailThreadSummary {
  total: number;
  inboundCount: number;
  outboundCount: number;
  latestInboundAt: string | null;
  latestOutboundAt: string | null;
  /** True when their newest message is newer than our newest reply. */
  unanswered: boolean;
}

export interface EmailThreadResponse {
  messages: EmailMessageRecord[];
  summary: EmailThreadSummary;
}

export interface EmailProposal {
  linkId: number;
  linkType: EmailLinkType;
  quoteId: string | null;
  model: string | null;
  wtbRequestId: string | null;
  matchField: string | null;
  matchedText: string | null;
  confidence: number | null;
}

export interface PendingEmailRecord extends EmailMessageRecord {
  proposal: EmailProposal;
}

export interface MailboxSyncStatus {
  /** Which mail transport is wired up. "imap" (read) + SMTP (send). */
  provider: string;
  configured: boolean;
  connected: boolean;
  connectedUser: string;
  lastSyncAt: string | null;
  lastError: string | null;
  /** Server names the mailbox is connected through (never the password). */
  imapHost?: string | null;
  smtpHost?: string | null;
  /** Unused over IMAP (there is no API quota to wait out); kept so older UI code still type-checks. */
  syncPause?: {
    mode: "initial" | "delta";
    resumeAt: string;
    pausedAt: string;
  } | null;
  /** A historical sync stopped part-way; the next run continues from that point. */
  backfillPending?: boolean;
  /** A sync for this user is running right now (e.g. the automatic resume). */
  syncing?: boolean;
  folders: string[];
  autoLinkThresholds: Record<string, number>;
  indexedMessages: number;
  linkedCount: number;
  pendingCount: number;
}

export interface LinkedKey {
  key: string;
  count: number;
  wtbRequestId: string | null;
}

export interface PagedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

// The key that identifies one record. Exactly one shape is valid per link type.
export type RecordKey =
  | { linkType: "quote"; quoteId: string }
  | { linkType: "dealer"; model?: string; wtbRequestId?: string };

const keyToParams = (key: RecordKey): Record<string, string> => {
  if (key.linkType === "quote") {
    return { linkType: "quote", quoteId: key.quoteId };
  }
  return {
    linkType: "dealer",
    ...(key.model ? { model: key.model } : {}),
    ...(key.wtbRequestId ? { wtbRequestId: key.wtbRequestId } : {}),
  };
};

// ─── Mailbox connection (IMAP + SMTP) ───────────────────────────────────────

export const getMailboxStatus = async (): Promise<MailboxSyncStatus> => {
  const response = await API.get("/backend/emails/mailbox/status");
  return response.data;
};

export interface MailboxCredentials {
  email: string;
  /** Mailbox password, or an app password for Gmail / Google Workspace. */
  password: string;
  /** Optional - the backend guesses these from the address domain. */
  imapHost?: string;
  imapPort?: number;
  smtpHost?: string;
  smtpPort?: number;
}

/**
 * Saves the mailbox login. The backend tries it against both the IMAP and the
 * SMTP server first, so a wrong password or host comes back as an error here.
 */
export const connectMailbox = async (
  credentials: MailboxCredentials,
): Promise<{ connected: boolean; connectedUser: string; message: string }> => {
  const response = await API.post("/backend/emails/mailbox/connect", credentials);
  return response.data;
};

export const disconnectMailbox = async (): Promise<{ message: string }> => {
  const response = await API.post("/backend/emails/mailbox/disconnect");
  return response.data;
};

/** Omit `initial` for an incremental delta sync; pass it for the backfill. */
export const triggerEmailSync = async (payload?: {
  initial?: boolean;
  daysBack?: number;
}): Promise<Record<string, unknown>> => {
  const response = await API.post("/backend/emails/sync", payload || {});
  return response.data;
};

// ─── Linked history ──────────────────────────────────────────────────────────

export const getEmailThread = async (key: RecordKey): Promise<EmailThreadResponse> => {
  const response = await API.get("/backend/emails/thread", {
    params: keyToParams(key),
  });
  return response.data;
};

/** Record keys that already have linked mail — powers the tab's picker. */
export const getLinkedKeys = async (linkType: EmailLinkType): Promise<LinkedKey[]> => {
  const response = await API.get("/backend/emails/linked-keys", {
    params: { linkType },
  });
  return response.data;
};

// ─── Nothing-is-lost lists ───────────────────────────────────────────────────

export const getUnmatchedEmails = async (params: {
  linkType: EmailLinkType;
  search?: string;
  /** "out" narrows the list to Sent Mail — our replies, not the customer's. */
  direction?: "in" | "out";
  page?: number;
  limit?: number;
}): Promise<PagedResponse<EmailMessageRecord>> => {
  const response = await API.get("/backend/emails/unmatched", { params });
  return response.data;
};

/** Non-exact matches awaiting a one-click confirm. Never shown as linked. */
export const getPendingEmailLinks = async (params: {
  linkType: EmailLinkType;
  page?: number;
  limit?: number;
}): Promise<PagedResponse<PendingEmailRecord>> => {
  const response = await API.get("/backend/emails/pending", { params });
  return response.data;
};

// ─── Link mutations ──────────────────────────────────────────────────────────

/** One-click manual attach. Lands as "linked" — a human chose it. */
export const linkEmailToRecord = async (
  emailId: number,
  key: RecordKey,
): Promise<{ message: string }> => {
  const response = await API.post(`/backend/emails/${emailId}/link`, keyToParams(key));
  return response.data;
};

export const confirmEmailLink = async (linkId: number): Promise<{ message: string }> => {
  const response = await API.post(`/backend/emails/links/${linkId}/confirm`);
  return response.data;
};

export const rejectEmailLink = async (linkId: number): Promise<{ message: string }> => {
  const response = await API.post(`/backend/emails/links/${linkId}/reject`);
  return response.data;
};

export const unlinkEmail = async (linkId: number): Promise<{ message: string }> => {
  const response = await API.delete(`/backend/emails/links/${linkId}`);
  return response.data;
};

// ─── Attachments ─────────────────────────────────────────────────────────────

/**
 * Downloads an attachment through the portal (which reads it from the mail server) and
 * hands it to the browser. Goes through axios rather than a plain link so the
 * Authorization header is attached.
 */
export const downloadEmailAttachment = async (
  emailId: number,
  attachment: EmailAttachmentMeta,
): Promise<void> => {
  // Types a browser can render itself open in a new tab; anything else (Word,
  // Excel, zip…) has no viewer, so it still downloads.
  const viewableTypes: Record<string, string> = {
    pdf: "application/pdf",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    gif: "image/gif",
    webp: "image/webp",
    svg: "image/svg+xml",
    txt: "text/plain",
  };
  const name = attachment.name || "attachment";
  const viewType = viewableTypes[name.split(".").pop()?.toLowerCase() || ""];

  // Opened before the request so the popup blocker sees a direct click.
  const tab = viewType ? window.open("", "_blank") : null;

  try {
    const response = await API.get(
      `/backend/emails/${emailId}/attachments/${attachment.id}`,
      { responseType: "blob" },
    );

    const blob = viewType
      ? new Blob([response.data as Blob], { type: viewType })
      : (response.data as Blob);
    const objectUrl = URL.createObjectURL(blob);

    if (tab) {
      tab.location.href = objectUrl;
      // Leave the URL alive long enough for the new tab to load it.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
      return;
    }

    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = name;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(objectUrl);
  } catch (error: any) {
    tab?.close();
    // With responseType "blob" an error body arrives as a Blob, so the
    // server's message is unreadable unless it is decoded here.
    const body = error?.response?.data;
    if (body instanceof Blob) {
      try {
        const parsed = JSON.parse(await body.text());
        if (parsed?.message) error.response.data = parsed;
      } catch {
        // not JSON - keep the original error
      }
    }
    throw error;
  }
};

// ─── Reply ───────────────────────────────────────────────────────────────────

/** Total raw size the backend accepts across one reply's attachments. */
export const REPLY_ATTACHMENT_MAX_BYTES = 15 * 1024 * 1024;

/**
 * Sent over SMTP from the connected mailbox and saved to its Sent folder.
 *
 * With files attached this goes out as multipart/form-data so the bytes are
 * streamed rather than base64'd into a JSON string; without them it stays the
 * plain JSON POST it has always been.
 */
export const replyToEmail = async (
  emailId: number,
  payload: {
    body: string;
    replyAll?: boolean;
    to?: string;
    cc?: string;
    subject?: string;
    attachments?: File[];
  },
): Promise<{ message: string }> => {
  const { attachments = [], ...fields } = payload;
  const url = `/backend/emails/${emailId}/reply`;

  if (!attachments.length) {
    const response = await API.post(url, fields);
    return response.data;
  }

  const form = new FormData();
  form.append("body", fields.body);
  if (fields.replyAll) form.append("replyAll", "true");
  if (fields.to) form.append("to", fields.to);
  if (fields.cc) form.append("cc", fields.cc);
  if (fields.subject) form.append("subject", fields.subject);
  attachments.forEach((file) => form.append("attachments", file, file.name));

  // The boundary has to come from the browser, so the header is left unset.
  const response = await API.post(url, form);
  return response.data;
};
